import { useCallback, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import {
  useTalentPool,
  useTalentPoolStats,
  useCreateTalentPoolCandidate,
  useConvertToApplication,
  useJobs,
} from '../hooks/useApi';
import { useToast } from '../contexts/ToastContext';
import { SearchInput } from '../components/ui/SearchInput';
import {
  Users,
  RefreshCw,
  ChevronLeft,
  ChevronRight,
  Plus,
  Mail,
  Phone,
  MapPin,
  FileText,
  ArrowRightCircle,
  X,
  Search,
  UserPlus,
} from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import { SkeletonCardGrid } from '../components/ui/Skeleton';
import type { TalentPoolCandidate } from '../types';

function AddCandidateModal({
  isOpen,
  onClose,
}: {
  isOpen: boolean;
  onClose: () => void;
}) {
  const { showToast } = useToast();
  const createMutation = useCreateTalentPoolCandidate();
  const [form, setForm] = useState({
    firstName: '',
    lastName: '',
    email: '',
    phone: '',
    location: '',
    notes: '',
  });

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!form.firstName.trim() || !form.lastName.trim() || !form.email.trim()) {
      showToast('First name, last name, and email are required', 'error');
      return;
    }

    try {
      await createMutation.mutateAsync({
        firstName: form.firstName.trim(),
        lastName: form.lastName.trim(),
        email: form.email.trim(),
        phone: form.phone.trim() || undefined,
        location: form.location.trim() || undefined,
        notes: form.notes.trim() || undefined,
      });
      showToast('Candidate added to talent pool', 'success');
      setForm({ firstName: '', lastName: '', email: '', phone: '', location: '', notes: '' });
      onClose();
    } catch (err) {
      const error = err as Error;
      showToast(`Failed to add candidate: ${error.message}`, 'error');
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 dark:bg-black/70">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Add to Talent Pool
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                First Name *
              </label>
              <input
                type="text"
                value={form.firstName}
                onChange={(e) => setForm((f) => ({ ...f, firstName: e.target.value }))}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Last Name *
              </label>
              <input
                type="text"
                value={form.lastName}
                onChange={(e) => setForm((f) => ({ ...f, lastName: e.target.value }))}
                className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
                required
              />
            </div>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Email *
            </label>
            <input
              type="email"
              value={form.email}
              onChange={(e) => setForm((f) => ({ ...f, email: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              required
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Phone
            </label>
            <input
              type="text"
              value={form.phone}
              onChange={(e) => setForm((f) => ({ ...f, phone: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Location
            </label>
            <input
              type="text"
              value={form.location}
              onChange={(e) => setForm((f) => ({ ...f, location: e.target.value }))}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Notes
            </label>
            <textarea
              value={form.notes}
              onChange={(e) => setForm((f) => ({ ...f, notes: e.target.value }))}
              rows={3}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
              placeholder="Notes about this candidate..."
            />
          </div>

          <div className="flex justify-end gap-3 pt-2">
            <button
              type="button"
              onClick={onClose}
              className="btn btn-secondary"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={createMutation.isPending}
              className="btn btn-primary"
            >
              {createMutation.isPending ? 'Adding...' : 'Add to Pool'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

function ConvertModal({
  isOpen,
  onClose,
  candidate,
}: {
  isOpen: boolean;
  onClose: () => void;
  candidate: TalentPoolCandidate | null;
}) {
  const { showToast } = useToast();
  const convertMutation = useConvertToApplication();
  const { data: jobsData } = useJobs({ limit: 100, status: 'active' });
  const [selectedJobId, setSelectedJobId] = useState<number | null>(null);

  const handleConvert = async () => {
    if (!candidate || !selectedJobId) {
      showToast('Please select a job', 'error');
      return;
    }

    try {
      await convertMutation.mutateAsync({
        candidateId: candidate.id,
        jobId: selectedJobId,
      });
      showToast(
        `${candidate.firstName} ${candidate.lastName} converted to application`,
        'success'
      );
      setSelectedJobId(null);
      onClose();
    } catch (err) {
      const error = err as Error;
      showToast(`Conversion failed: ${error.message}`, 'error');
    }
  };

  if (!isOpen || !candidate) return null;

  const jobs = jobsData?.data || [];

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-gray-900/50 dark:bg-black/70">
      <div className="w-full max-w-md rounded-lg bg-white dark:bg-gray-800 p-6 shadow-xl">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">
            Convert to Application
          </h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
            <X className="h-5 w-5" />
          </button>
        </div>

        <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
          Create an application for{' '}
          <span className="font-medium text-gray-900 dark:text-gray-100">
            {candidate.firstName} {candidate.lastName}
          </span>{' '}
          by selecting a job below.
        </p>

        <div className="mb-4">
          <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
            Select Job *
          </label>
          {jobs.length === 0 ? (
            <p className="text-sm text-gray-500 dark:text-gray-400">
              No active jobs available. Create a job first.
            </p>
          ) : (
            <select
              value={selectedJobId ?? ''}
              onChange={(e) => setSelectedJobId(e.target.value ? Number(e.target.value) : null)}
              className="w-full rounded-md border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 px-3 py-2 text-sm text-gray-900 dark:text-gray-100 focus:border-primary-500 focus:ring-1 focus:ring-primary-500"
            >
              <option value="">-- Select a job --</option>
              {jobs.map((job) => (
                <option key={job.id} value={job.id}>
                  {job.title} {job.department ? `(${job.department})` : ''}
                </option>
              ))}
            </select>
          )}
        </div>

        <div className="flex justify-end gap-3 pt-2">
          <button type="button" onClick={onClose} className="btn btn-secondary">
            Cancel
          </button>
          <button
            onClick={handleConvert}
            disabled={!selectedJobId || convertMutation.isPending}
            className="btn btn-primary disabled:opacity-50"
          >
            {convertMutation.isPending ? 'Converting...' : 'Convert'}
          </button>
        </div>
      </div>
    </div>
  );
}

function TalentPoolCard({
  candidate,
  onConvert,
}: {
  candidate: TalentPoolCandidate;
  onConvert: (candidate: TalentPoolCandidate) => void;
}) {
  const name = `${candidate.firstName} ${candidate.lastName}`.trim();

  return (
    <div className="group relative rounded-lg border bg-white dark:bg-gray-800 p-3 sm:p-4 transition-all hover:shadow-md border-gray-200 dark:border-gray-700 hover:border-primary-300 dark:hover:border-primary-700">
      <div className="flex items-start justify-between gap-2 sm:gap-3">
        {/* Left: Avatar + Name + Contact */}
        <Link to={`/people/${candidate.id}`} className="flex items-start gap-2 sm:gap-3 min-w-0 flex-1">
          {/* Avatar */}
          <div className="flex h-9 w-9 sm:h-10 sm:w-10 shrink-0 items-center justify-center rounded-full bg-gradient-to-br from-violet-100 to-violet-200 dark:from-violet-900 dark:to-violet-800 text-xs sm:text-sm font-bold text-violet-700 dark:text-violet-300">
            {candidate.firstName.charAt(0)}{candidate.lastName.charAt(0)}
          </div>

          <div className="min-w-0">
            <h3 className="font-semibold text-sm sm:text-base text-gray-900 dark:text-gray-100 group-hover:text-primary-600 dark:group-hover:text-primary-400 transition-colors truncate">
              {name}
            </h3>
            <div className="mt-0.5 flex flex-wrap items-center gap-x-2 sm:gap-x-3 gap-y-0.5 text-xs text-gray-500 dark:text-gray-400">
              <span className="flex items-center gap-1 truncate max-w-[180px] sm:max-w-none">
                <Mail className="h-3 w-3 shrink-0" />
                <span className="truncate">{candidate.email}</span>
              </span>
              {candidate.phone && (
                <span className="hidden sm:flex items-center gap-1">
                  <Phone className="h-3 w-3 shrink-0" />
                  {candidate.phone}
                </span>
              )}
              {candidate.location && (
                <span className="hidden sm:flex items-center gap-1 truncate">
                  <MapPin className="h-3 w-3 shrink-0" />
                  {candidate.location}
                </span>
              )}
            </div>
          </div>
        </Link>

        {/* Right: Convert button */}
        <button
          onClick={(e) => {
            e.preventDefault();
            e.stopPropagation();
            onConvert(candidate);
          }}
          className="shrink-0 inline-flex items-center gap-1.5 rounded-md border border-primary-300 dark:border-primary-700 bg-white dark:bg-gray-800 px-2.5 py-1.5 text-xs font-medium text-primary-700 dark:text-primary-300 hover:bg-primary-50 dark:hover:bg-primary-950 transition-colors"
          title="Convert to Application"
        >
          <ArrowRightCircle className="h-3.5 w-3.5" />
          <span className="hidden sm:inline">Convert</span>
        </button>
      </div>

      {/* Notes */}
      {candidate.talentPoolNotes && (
        <div className="mt-2 flex items-start gap-1.5 text-xs text-gray-500 dark:text-gray-400">
          <FileText className="h-3 w-3 shrink-0 mt-0.5" />
          <span className="line-clamp-2">{candidate.talentPoolNotes}</span>
        </div>
      )}

      {/* Bottom: Added date */}
      <div className="mt-2 sm:mt-3 flex items-center justify-between text-xs text-gray-500 dark:text-gray-400">
        <span className="inline-flex items-center gap-1 rounded-full bg-violet-50 dark:bg-violet-950 text-violet-600 dark:text-violet-400 px-2 py-0.5 text-[10px] font-medium">
          Talent Pool
        </span>
        {candidate.addedToPoolAt && (
          <span className="truncate ml-2">
            Added {formatDistanceToNow(new Date(candidate.addedToPoolAt), { addSuffix: true })}
          </span>
        )}
      </div>
    </div>
  );
}

export default function TalentPool() {
  const [searchParams, setSearchParams] = useSearchParams();
  const search = searchParams.get('search') || '';
  const page = parseInt(searchParams.get('page') || '1', 10);
  const limit = 20;

  const [showAddModal, setShowAddModal] = useState(false);
  const [convertCandidate, setConvertCandidate] = useState<TalentPoolCandidate | null>(null);

  const updateFilters = useCallback(
    (updates: Record<string, string>) => {
      const newParams = new URLSearchParams(searchParams);
      Object.entries(updates).forEach(([key, value]) => {
        if (value) {
          newParams.set(key, value);
        } else {
          newParams.delete(key);
        }
      });
      if (!('page' in updates)) {
        newParams.set('page', '1');
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams]
  );

  const handleSearchChange = useCallback(
    (value: string) => updateFilters({ search: value }),
    [updateFilters]
  );
  const handlePageChange = useCallback(
    (newPage: number) => updateFilters({ page: String(newPage) }),
    [updateFilters]
  );

  const { data, isLoading, isFetching, error, refetch } = useTalentPool({
    page,
    limit,
    search: search || undefined,
  });

  const { data: stats } = useTalentPoolStats();

  if (error) {
    return (
      <div className="card dark:bg-gray-800 dark:border-gray-700 p-8 text-center">
        <Users className="mx-auto h-10 w-10 text-red-400 dark:text-red-500" />
        <p className="mt-3 text-red-600 dark:text-red-400 font-medium">Failed to load talent pool</p>
        <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">{error.message}</p>
        <button onClick={() => refetch()} className="btn btn-primary mt-4">
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between gap-3">
        <div className="min-w-0">
          <h2 className="text-lg font-medium text-gray-900 dark:text-gray-100">Talent Pool</h2>
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {stats?.totalPoolCandidates ?? 0} candidates in pool
            {search && ' (filtered)'}
          </p>
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() => setShowAddModal(true)}
            className="btn btn-primary shrink-0"
          >
            <UserPlus className="mr-2 h-4 w-4" />
            <span className="hidden sm:inline">Add Candidate</span>
            <span className="sm:hidden">Add</span>
          </button>
          <button
            onClick={() => refetch()}
            className="btn btn-secondary shrink-0"
            disabled={isLoading}
          >
            <RefreshCw className={`h-4 w-4 ${isLoading ? 'animate-spin' : ''}`} />
          </button>
        </div>
      </div>

      {/* Search */}
      <div className="card p-4 overflow-hidden dark:bg-gray-800 dark:border-gray-700">
        <div className="w-full sm:max-w-md">
          <SearchInput
            value={search}
            onChange={handleSearchChange}
            placeholder="Search by name or email..."
            label="Search"
            isLoading={isFetching}
          />
        </div>
      </div>

      {/* Talent Pool Grid */}
      {isLoading ? (
        <SkeletonCardGrid cards={9} columns={3} />
      ) : data?.data.length === 0 ? (
        <div className="card p-12 text-center dark:bg-gray-800 dark:border-gray-700">
          {search ? (
            <>
              <Search className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-gray-500">No talent pool candidates match your search</p>
              <button
                onClick={() => handleSearchChange('')}
                className="mt-3 text-sm text-primary-600 hover:text-primary-700"
              >
                Clear search
              </button>
            </>
          ) : (
            <>
              <Users className="mx-auto h-8 w-8 text-gray-400" />
              <p className="mt-2 text-gray-500">No candidates in the talent pool yet</p>
              <p className="text-sm text-gray-400 mt-1">
                Add candidates who are not yet linked to a specific job.
              </p>
              <button
                onClick={() => setShowAddModal(true)}
                className="btn btn-primary mt-4"
              >
                <Plus className="mr-2 h-4 w-4" />
                Add First Candidate
              </button>
            </>
          )}
        </div>
      ) : (
        <div className="grid gap-3 grid-cols-1 lg:grid-cols-2 xl:grid-cols-3">
          {data?.data.map((candidate) => (
            <TalentPoolCard
              key={candidate.id}
              candidate={candidate}
              onConvert={setConvertCandidate}
            />
          ))}
        </div>
      )}

      {/* Pagination */}
      {data && data.pagination.totalPages > 1 && (
        <div className="flex flex-col sm:flex-row items-center justify-between gap-3">
          <div className="text-sm text-gray-500 dark:text-gray-400 text-center sm:text-left">
            Showing{' '}
            <span className="font-medium">{(page - 1) * limit + 1}</span>
            {' '}-{' '}
            <span className="font-medium">{Math.min(page * limit, data.pagination.total)}</span>
            {' '}of <span className="font-medium">{data.pagination.total}</span>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => handlePageChange(Math.max(1, page - 1))}
              disabled={page === 1}
              className="btn btn-secondary disabled:opacity-50"
            >
              <ChevronLeft className="h-4 w-4" />
            </button>
            <button
              onClick={() => handlePageChange(Math.min(data.pagination.totalPages, page + 1))}
              disabled={page === data.pagination.totalPages}
              className="btn btn-secondary disabled:opacity-50"
            >
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Modals */}
      <AddCandidateModal
        isOpen={showAddModal}
        onClose={() => setShowAddModal(false)}
      />
      <ConvertModal
        isOpen={!!convertCandidate}
        onClose={() => setConvertCandidate(null)}
        candidate={convertCandidate}
      />
    </div>
  );
}
